import { Loader2 } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useCampaignClone, useUserModules } from '../api';
import { ComposeWizard } from '../components/ComposeWizard';
import { hydrateFromClonePayload } from '../hydrate';

/** Clone API returns either the full payload or `{ message }` when blocked. */
function isBlocked(d: unknown): d is { message: string } {
  return typeof d === 'object' && d !== null && 'message' in d;
}

/** Pull `{ message }` out of an axios error body (403/404 path), if present. */
function extractApiMessage(err: unknown): string | undefined {
  const msg = (err as { response?: { data?: { message?: unknown } } } | null)?.response?.data
    ?.message;
  return typeof msg === 'string' && msg !== '' ? msg : undefined;
}

export function CloneVoiceCampaignPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const modules = useUserModules();

  const moduleNames = useMemo(
    () => new Set((modules.data ?? []).map((m) => m.moduleName)),
    [modules.data],
  );

  const isContinuous = moduleNames.has('Continuous Campaign');

  const id = Number(campaignId);
  const validId = Number.isInteger(id) && id > 0;
  const clone = useCampaignClone(validId ? id : 0);

  const blocked = clone.data !== undefined && isBlocked(clone.data);

  /* Module gate returned 200 + { message } → toast + leave. */
  useEffect(() => {
    if (blocked && isBlocked(clone.data)) {
      toast.error(clone.data.message);
      navigate(-1); // ← your campaign list route
    }
  }, [blocked, clone.data, navigate]);

  /* Route answered 403/404 → axios threw → same UX via the error path. */
  useEffect(() => {
    if (!clone.isError) return;
    toast.error(extractApiMessage(clone.error) ?? 'Could not load the campaign for cloning.');
    navigate(-1);
  }, [clone.isError, clone.error, navigate]);

  const initial = useMemo(() => {
    if (!clone.data || isBlocked(clone.data)) return undefined;
    const base = hydrateFromClonePayload(clone.data, isContinuous);
    // Name and voice-file selections are left blank for the user to pick
    // fresh — everything else carries over from the source campaign.
    return {
      ...base,
      campaignName: '',
      useWelcomeFile: false,
      useNoinputFile: false,
      useWronginputFile: false,
      useThanksFile: false,
      welcomeId: '',
      menuId: '',
      noinputId: '',
      wronginputId: '',
      thanksId: '',
      noAgentId: '',
    };
  }, [clone.data, isContinuous]);

  // Errored/blocked → redirecting away, render nothing (avoids a fake spinner frame).
  if (clone.isError || blocked) return null;

  if (!initial) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading campaign…
      </div>
    );
  }

  return (
    <ComposeWizard
      mode="clone"
      initialValues={initial}
      pageTitle={`Clone Campaign #${id}`}
      pageDescription="Re-use the settings from an existing campaign and tweak as needed."
    />
  );
}
